import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { sortBy } from 'lodash';

export abstract class CrudService<T extends { id?: number }> {
  values$: BehaviorSubject<T[]> = new BehaviorSubject(null);

  protected constructor(protected _http: HttpClient, protected cache = true) {}

  abstract get url(): string;

  /**
   * This function is used to order the items again after editing or adding an item
   */
  get orderByProperty() {
    return 'id';
  }

  list(): Observable<T[]> {
    if (this.values$.value && this.cache) {
      return this.values$.asObservable();
    }

    return this._http.get<T[]>(`${this.url}`).pipe(
      catchError(error => of([])),
      switchMap((values: T[]) => {
        this.values$.next(values);
        return this.values$;
      })
    );
  }

  get(valueId: number): Observable<T> {
    return this._http.get<T>(`${this.url}/${valueId}`);
  }

  add(value: T): Observable<T> {
    return this.addRequest(value).pipe(
      tap((_value: T) => this.addValue(_value))
    );
  }

  edit(value: T): Observable<T> {
    return this.editRequest(value).pipe(
      tap((_value: T) => this.updateValue(value, _value))
    );
  }

  delete(valueId: number) {
    return this.deleteRequest(valueId).pipe(
      tap(() => this.removeValue(valueId))
    );
  }

  protected addValue(_value: T): T {
    if (this.values$.value) {
      const valueIndex: number = this.findAddedValueIndex(_value);
      let values: T[];
      values = [...this.values$.value];
      if (valueIndex > -1) {
        values.splice(valueIndex, 0, _value);
      } else {
        values.push(_value);
      }
      this.values$.next(values);
    }
    return _value;
  }

  protected addRequest(value: T) {
    return this._http.post<T>(`${this.url}`, value);
  }

  protected editRequest(value: T) {
    return this._http.patch<T>(`${this.url}/${value.id}`, value);
  }

  protected deleteRequest(valueId: number) {
    return this._http.delete(`${this.url}/${valueId}`);
  }

  removeValue(valueId: number) {
    if (this.values$.value) {
      const values: T[] = this.values$.value.filter(
        value => value.id !== valueId
      );
      this.values$.next(values);
    }
  }

  updateValue(value: T, _value: T): T {
    if (this.values$.value) {
      const values: T[] = [...this.values$.value];
      const valueIndex: number = values.findIndex(
        (item: T) => item.id === value.id
      );
      values[valueIndex] = _value;
      this.values$.next(this.sortValues(values));
    }
    return _value;
  }

  private sortValues(values: T[]) {
    const property: string = this.orderByProperty;
    return sortBy(values, [property]);
  }

  private findAddedValueIndex(_value: T) {
    const property: string = this.orderByProperty;
    return this.values$.value.findIndex(value => {
      if (typeof value[property] === 'string') {
        return value[property].toLowerCase() > _value[property].toLowerCase();
      }

      return value[property] > _value[property];
    });
  }
}
